import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import SecurePdfViewer from "../../../components/Atomos/PdfPreview/SecurePdfViewer";
import { useDynamicPageMeta } from "../../../hooks/useDynamicPageMeta";
import { useEventLoader } from "../../../hooks/Events/useEventLoader";
import { fetchPresentationPdf } from "../../../Services/Event";
import { normalizeDocumentUrl } from "../../../utils/normalizeDocumentUrl";

const HERO_TITLE_MAX_LENGTH = 40;

const truncateHeroTitle = (title: string): string => {
  if (title.length <= HERO_TITLE_MAX_LENGTH) {
    return title;
  }

  return `${title.slice(0, HERO_TITLE_MAX_LENGTH - 3).trimEnd()}...`;
};

export default function EventPresentationViewer(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const programId = searchParams.get("programId") ?? undefined;
  const event = useEventLoader(id);
  const { setPageMeta, clearPageMeta } = useDynamicPageMeta();
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const presentationProgram = useMemo(() => {
    return event?.program?.find((program) => {
      if (programId) {
        return program.id === programId && program.documents && program.documents.length > 0;
      }
      return program.documents && program.documents.length > 0;
    });
  }, [event, programId]);

  const documentUrl = useMemo(() => {
    const rawUrl = presentationProgram?.documents?.[0];
    return rawUrl ? normalizeDocumentUrl(rawUrl) : "";
  }, [presentationProgram]);

  useEffect(() => {
    if (!id || !event?.title) {
      return;
    }

    setPageMeta({
      title: truncateHeroTitle(event.title),
      breadcrumbLabels: {
        [`/events/${id}/program/pdf`]: event.title,
      },
    });

    return () => {
      clearPageMeta();
    };
  }, [clearPageMeta, event?.title, id, setPageMeta]);

  useEffect(() => {
    if (!id || !event) {
      return;
    }

    if (!presentationProgram || !documentUrl) {
      setLoading(false);
      setError("Presentation is not available for this event.");
      return;
    }

    let cancelled = false;

    const loadPresentation = async () => {
      setLoading(true);
      setError("");
      try {
        const blob = await fetchPresentationPdf(id, documentUrl, programId);
        if (!cancelled) {
          setPdfBlob(blob);
        }
      } catch {
        if (!cancelled) {
          setError(
            "Unable to load the presentation securely. Upload the PDF to Azure Blob storage for this event, then try again.",
          );
          setPdfBlob(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadPresentation();

    return () => {
      cancelled = true;
    };
  }, [id, event, programId, presentationProgram, documentUrl]);

  if (!event) {
    return <div className="p-8 text-gray-font">Event not found</div>;
  }

  return (
    <div className="pb-8 w-full">
      <div className="mb-6 flex flex-col gap-3">
        <Link
          to={`/events/${id}/program`}
          className="text-[#0070CD] font-Roboto text-sm font-semibold hover:underline w-fit"
        >
          Back to Program
        </Link>
      </div>

      {error ? (
        <div className="flex min-h-[32rem] items-center justify-center px-4 text-center text-sm text-red-600">
          {error}
        </div>
      ) : (
        <SecurePdfViewer
          pdfBlob={pdfBlob}
          loading={loading}
          className="rounded-lg border border-gray-200"
        />
      )}
    </div>
  );
}
